import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { MainLayout } from '../../components/layout/MainLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Users, Package, ShoppingCart, TrendingUp, Plus, FileDown } from 'lucide-react'
import { dashboardService } from '../../services/dashboardService'
import { industriaService } from '../../services/industriaService'
import { pedidoService } from '../../services/pedidoService'
import { useWebSocketStore } from '../../store/websocketStore'
import { useAuthStore } from '../../store/authStore'
import type { DashboardStats, Pedido, Industria } from '../../types'
import { CardSkeleton } from '../../components/ui/Skeleton'
import { toast } from '../../utils/toast'
import { getIndustriaColorClasses, getIndustriaInlineStyles } from '../../utils/helpers'

export function DashboardPage() {
    const navigate = useNavigate()
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const [stats, setStats] = useState<DashboardStats | null>(null)
    const [pedidos, setPedidos] = useState<Pedido[]>([])
    const [industrias, setIndustrias] = useState<Industria[]>([])
    const [industriaId, setIndustriaId] = useState('')
    const [loading, setLoading] = useState(true)
    const [periodo, setPeriodo] = useState<'hoje' | 'semana' | 'mes' | '90dias'>('mes')
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [exporting, setExporting] = useState(false)

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'pendente':
                return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400'
            case 'processando':
                return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400'
            case 'concluido':
                return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
            case 'cancelado':
                return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
            default:
                return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
        }
    }

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'pendente':
                return 'Pendente'
            case 'processando':
                return 'Em Processamento'
            case 'concluido':
                return 'Concluído'
            case 'cancelado':
                return 'Cancelado'
            default:
                return status
        }
    }

    const renderIndustriaBadge = (industria: { nomeFantasia: string; cor?: string }) => {
        const classes = getIndustriaColorClasses(industria.cor)
        const inline = getIndustriaInlineStyles(industria.cor)
        return (
            <span
                key={industria.nomeFantasia}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold border ${classes.border} ${classes.bgLight} ${classes.text}`}
                style={inline ? {
                    borderColor: inline.borderColor,
                    backgroundColor: inline.backgroundColorLight,
                    color: inline.color,
                } : undefined}
            >
                <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: inline ? inline.cor : classes.cor }}
                />
                {industria.nomeFantasia}
            </span>
        )
    }

    const loadDashboardData = useCallback(async () => {
        if (!isAuthenticated) {
            console.log('⚠️ Usuário não autenticado, pulando carregamento do dashboard')
            return
        }

        setLoading(true)
        try {
            const [statsData, ultimosPedidosData] = await Promise.all([
                dashboardService.getStats(periodo),
                dashboardService.getUltimosPedidos(industriaId || undefined)
            ])

            setStats(statsData)
            setPedidos(ultimosPedidosData.pedidos as Pedido[])
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao carregar dashboard'
            toast.error(message)
            console.error('Erro ao carregar dados do dashboard:', error)
        } finally {
            setLoading(false)
            setIsInitialLoad(false)
        }
    }, [periodo, industriaId, isAuthenticated])

    const loadIndustrias = useCallback(async () => {
        try {
            const result = await industriaService.getAll({ status: 'ativo', limit: 1000 })
            setIndustrias(result.data)
        } catch (error) {
            console.error('Erro ao carregar indústrias:', error)
        }
    }, [])

    const getPeriodoDates = () => {
        const hoje = new Date()
        const dataFim = hoje.toISOString().split('T')[0]
        let dataInicio = dataFim

        switch (periodo) {
            case 'hoje':
                dataInicio = dataFim
                break
            case 'semana':
                dataInicio = new Date(hoje.setDate(hoje.getDate() - 7)).toISOString().split('T')[0]
                break
            case '90dias':
                dataInicio = new Date(hoje.setDate(hoje.getDate() - 90)).toISOString().split('T')[0]
                break
            case 'mes':
            default:
                dataInicio = new Date(hoje.setDate(hoje.getDate() - 30)).toISOString().split('T')[0]
                break
        }

        return { dataInicio, dataFim }
    }

    const exportarRelatorio = async () => {
        setExporting(true)
        try {
            const { dataInicio, dataFim } = getPeriodoDates()
            const blob = await pedidoService.exportarRelatorio({
                industriaId: industriaId || undefined,
                dataInicio,
                dataFim,
            })

            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `relatorio_pedidos_${dataInicio}_a_${dataFim}.xlsx`
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)

            toast.success('Relatório exportado com sucesso!')
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Erro ao exportar relatório'
            toast.error(message)
            console.error('Erro ao exportar relatório:', error)
        } finally {
            setExporting(false)
        }
    }

    useEffect(() => {
        loadIndustrias()
    }, [loadIndustrias])

    useEffect(() => {
        if (isInitialLoad && isAuthenticated) {
            loadDashboardData()
        }
    }, [isInitialLoad, isAuthenticated, loadDashboardData])

    // Recarrega quando o período ou indústria muda (após o load inicial)
    useEffect(() => {
        if (!isInitialLoad && isAuthenticated) {
            loadDashboardData()
        }
    }, [periodo, industriaId, isAuthenticated])

    // WebSocket: Atualização em tempo real
    useEffect(() => {
        const componentId = 'dashboard-page'

        // Registra callbacks no store
        useWebSocketStore.getState().onPedidoCriado(componentId, (data) => {
            console.log('🔄 [Dashboard] Novo pedido criado via WebSocket:', data.pedidoId)
            loadDashboardData()
        })

        useWebSocketStore.getState().onPedidoStatusAlterado(componentId, (data) => {
            console.log('🔄 [Dashboard] Status alterado via WebSocket:', data.pedidoId, data.statusNovo)

            // Atualiza o pedido na lista local
            setPedidos(prevPedidos =>
                prevPedidos.map(pedido =>
                    pedido.id === data.pedidoId
                        ? { ...pedido, status: data.statusNovo as Pedido['status'] }
                        : pedido
                )
            )

            // Recarrega stats (contadores de status podem ter mudado)
            dashboardService.getStats(periodo).then(setStats).catch(console.error)
        })

        useWebSocketStore.getState().onPedidoCancelado(componentId, (data) => {
            console.log('🔄 [Dashboard] Pedido cancelado via WebSocket:', data.pedidoId)

            // Atualiza o status para cancelado
            setPedidos(prevPedidos =>
                prevPedidos.map(pedido =>
                    pedido.id === data.pedidoId
                        ? { ...pedido, status: 'cancelado' as Pedido['status'] }
                        : pedido
                )
            )

            // Recarrega stats
            dashboardService.getStats(periodo).then(setStats).catch(console.error)
        })

        // Cleanup: Remove callbacks quando o componente desmonta
        return () => {
            useWebSocketStore.getState().offPedidoCriado(componentId)
            useWebSocketStore.getState().offPedidoStatusAlterado(componentId)
            useWebSocketStore.getState().offPedidoCancelado(componentId)
        }
    }, [loadDashboardData, periodo])

    if (loading && isInitialLoad) {
        return (
            <MainLayout>
                <div className="space-y-6">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Dashboard
                    </h1>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mx-2">
                        {[1, 2, 3, 4].map((i) => (
                            <CardSkeleton key={i} />
                        ))}
                    </div>
                </div>
            </MainLayout>
        )
    }

    return (
        <MainLayout>
            <div className="space-y-6 animate-fadeIn">
                <div className="flex flex-col gap-4 mx-2 sm:flex-row sm:items-center sm:justify-between">
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                        Dashboard
                    </h1>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                        <select
                            value={industriaId}
                            onChange={(e) => setIndustriaId(e.target.value)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:w-auto"
                        >
                            <option value="">Todas as indústrias</option>
                            {industrias.map(industria => (
                                <option key={industria.id} value={industria.id}>
                                    {industria.nomeFantasia}
                                </option>
                            ))}
                        </select>
                        <select
                            value={periodo}
                            onChange={(e) => setPeriodo(e.target.value as any)}
                            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:w-auto"
                        >
                            <option value="hoje">Hoje</option>
                            <option value="semana">Última Semana</option>
                            <option value="mes">Último Mês</option>
                            <option value="90dias">Últimos 90 dias</option>
                        </select>
                        <Button
                            variant="outline"
                            className="w-full sm:w-auto"
                            onClick={exportarRelatorio}
                            isLoading={exporting}
                            disabled={exporting || pedidos.length === 0}
                        >
                            <FileDown className="mr-2" />
                            Exportar
                        </Button>
                        <Button className="w-full sm:w-auto" onClick={() => navigate('/pedidos/novo')}>
                            <Plus className="mr-2" />
                            Novo Pedido
                        </Button>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 animate-slideIn">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex-1">
                                <p className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400">
                                    Total de Pedidos
                                </p>
                                <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold text-blue-900 dark:text-blue-100">
                                    {stats?.totalPedidos || 0}
                                </p>
                            </div>
                            <ShoppingCart className="h-8 w-8 sm:h-12 sm:w-12 text-blue-600 opacity-50" />
                        </div>
                    </Card>

                    <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex-1">
                                <p className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-400">
                                    Clientes Ativos
                                </p>
                                <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold text-green-900 dark:text-green-100">
                                    {stats?.totalClientes || 0}
                                </p>
                            </div>
                            <Users className="h-8 w-8 sm:h-12 sm:w-12 text-green-600 opacity-50" />
                        </div>
                    </Card>

                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex-1">
                                <p className="text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400">
                                    Última Semana
                                </p>
                                <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold text-purple-900 dark:text-purple-100">
                                    {stats?.pedidosUltimaSemana || 0}
                                </p>
                            </div>
                            <TrendingUp className="h-8 w-8 sm:h-12 sm:w-12 text-purple-600 opacity-50" />
                        </div>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex-1">
                                <p className="text-xs sm:text-sm font-medium text-orange-600 dark:text-orange-400">
                                    Último Mês
                                </p>
                                <p className="mt-1 sm:mt-2 text-2xl sm:text-3xl font-bold text-orange-900 dark:text-orange-100">
                                    {stats?.pedidosUltimoMes || 0}
                                </p>
                            </div>
                            <Package className="h-8 w-8 sm:h-12 sm:w-12 text-orange-600 opacity-50" />
                        </div>
                    </Card>
                </div>
                {/* Tabs por Indústria para Últimos Pedidos */}
                <div className="flex flex-wrap gap-2 animate-fadeIn">
                    <button
                        onClick={() => setIndustriaId('')}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                            industriaId === ''
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                        }`}
                    >
                        Todas
                    </button>
                    {industrias.map(industria => (
                        <button
                            key={industria.id}
                            onClick={() => setIndustriaId(industria.id)}
                            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                                industriaId === industria.id
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'
                            }`}
                        >
                            {industria.cor && (
                                <span
                                    className="h-2.5 w-2.5 rounded-full"
                                    style={{ backgroundColor: industria.cor }}
                                />
                            )}
                            {industria.nomeFantasia}
                        </button>
                    ))}
                </div>

                {/* Últimos Pedidos */}
                <Card title={`Últimos Pedidos ${industriaId ? `- ${industrias.find(i => i.id === industriaId)?.nomeFantasia || ''}` : '- Todas as Indústrias'}`}>
                    {/* Desktop Table */}
                    <div className="hidden md:block overflow-x-auto animate-fadeIn">
                        <table className="w-full">
                            <thead className="border-b border-gray-200 dark:border-gray-700">
                                <tr>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Nº Pedido
                                    </th>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Data
                                    </th>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Cliente
                                    </th>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Indústria
                                    </th>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Itens
                                    </th>
                                    <th className="pb-3 text-left text-sm font-semibold text-gray-900 dark:text-white">
                                        Status
                                    </th>
                                    <th className="pb-3 text-right text-sm font-semibold text-gray-900 dark:text-white">
                                        Ações
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                {pedidos && pedidos.length > 0 ? pedidos.slice(0, 10).map((pedido) => (
                                    <tr
                                        key={pedido.id}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                        <td className="py-4 text-sm font-mono font-semibold text-gray-900 dark:text-white">
                                            #{pedido.numero || pedido.id.slice(0, 8).toUpperCase()}
                                        </td>
                                        <td className="py-4 text-sm text-gray-900 dark:text-white">
                                            {pedido.createdAt ? new Date(pedido.createdAt).toLocaleDateString('pt-BR') : '-'}
                                        </td>
                                        <td className="py-4 text-sm font-medium text-gray-900 dark:text-white">
                                            {pedido.cliente?.razaoSocial || 'Cliente não encontrado'}
                                        </td>
                                        <td className="py-4">
                                            <div className="flex flex-wrap gap-1.5">
                                                {pedido.industrias && pedido.industrias.length > 0
                                                    ? pedido.industrias.map(i => renderIndustriaBadge({ nomeFantasia: i.nomeFantasia || i.industria?.nomeFantasia || '-', cor: i.cor || i.industria?.cor }))
                                                    : '-'
                                                }
                                            </div>
                                        </td>
                                        <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                                            {pedido.industrias?.[0]?.totalItens ?? pedido.total ?? 0} {(pedido.industrias?.[0]?.totalItens ?? pedido.total ?? 0) === 1 ? 'item' : 'itens'}
                                        </td>
                                        <td className="py-4">
                                            <span className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(pedido.status)}`}>
                                                {getStatusLabel(pedido.status)}
                                            </span>
                                        </td>
                                        <td className="py-4 text-right">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(`/pedidos/editar/${pedido.id}`)}
                                            >
                                                Ver
                                            </Button>
                                        </td>
                                    </tr>
                                )) : null}
                            </tbody>
                        </table>

                        {pedidos.length === 0 && (
                            <div className="py-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">
                                    Nenhum pedido encontrado
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Mobile Cards */}
                    <div className="md:hidden space-y-3 animate-fadeIn">
                        {pedidos && pedidos.length > 0 ? (
                            pedidos.slice(0, 10).map((pedido) => (
                                <div
                                    key={pedido.id}
                                    className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3"
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            <h4 className="font-mono font-semibold text-sm text-gray-900 dark:text-white">
                                                #{pedido.numero || pedido.id.slice(0, 8).toUpperCase()}
                                            </h4>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                {pedido.createdAt ? new Date(pedido.createdAt).toLocaleDateString('pt-BR') : '-'}
                                            </p>
                                        </div>
                                        <span className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(pedido.status)}`}>
                                            {getStatusLabel(pedido.status)}
                                        </span>
                                    </div>
                                    
                                    <div className="space-y-2 text-xs mb-3">
                                        <div className="flex flex-wrap gap-1.5">
                                            {pedido.industrias && pedido.industrias.length > 0
                                                ? pedido.industrias.map(i => renderIndustriaBadge({ nomeFantasia: i.nomeFantasia || i.industria?.nomeFantasia || '-', cor: i.cor || i.industria?.cor }))
                                                : '-'
                                            }
                                        </div>
                                        <p className="text-gray-900 dark:text-white font-medium truncate">
                                            {pedido.cliente?.razaoSocial || 'Cliente não encontrado'}
                                        </p>
                                        <p className="text-gray-600 dark:text-gray-400">
                                            {pedido.industrias?.[0]?.totalItens ?? pedido.total ?? 0} {(pedido.industrias?.[0]?.totalItens ?? pedido.total ?? 0) === 1 ? 'item' : 'itens'}
                                        </p>
                                    </div>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full text-xs"
                                        onClick={() => navigate(`/pedidos/editar/${pedido.id}`)}
                                    >
                                        Ver Detalhes
                                    </Button>
                                </div>
                            ))
                        ) : (
                            <div className="py-12 text-center">
                                <p className="text-gray-500 dark:text-gray-400">
                                    Nenhum pedido encontrado
                                </p>
                            </div>
                        )}
                    </div>
                </Card>


            </div>
        </MainLayout>
    )
}
